import type { RelationshipGraphData, RelationshipGraphGroup, RelationshipPair, RelationshipPlayer } from '../types/relationships';

export const GRAPH_NODE_LIMIT = 120;
export const GRAPH_EDGE_LIMIT = 250;
/** How many of a focused player's strongest connections are drawn before "more" is requested. */
export const EGO_CONNECTION_STEP = 12;
/** Centre-to-centre spacing that leaves room for a bubble, its name label and a gap. */
export const GRAPH_MIN_SEPARATION = 118;
/** Distance a group outline keeps from the centre of its members. */
export const GRAPH_HULL_PADDING = 42;
const RING_INNER = 165, RING_STEP = 150, RING_ONE_CAPACITY = 8;
const CLUSTER_GAP = GRAPH_HULL_PADDING * 2 + 16, BRIDGE_GAP = GRAPH_MIN_SEPARATION * 2 - 68;
const GOLDEN_ANGLE = 2.399963, SOLO = 'solo';

export interface GraphPoint { x: number; y: number }
export interface GraphParticle extends GraphPoint { id: string; vx: number; vy: number; anchorX: number; anchorY: number; pinned?: GraphPoint }
export interface GraphCamera { x: number; y: number; scale: number }
export interface GraphLayout { anchors: Map<string, GraphPoint>; rings: number[] }
export interface RankedConnection {
  edge: RelationshipPair;
  other: RelationshipPlayer;
  /** Counted as stronger because both players share a discovered group, not because the pair itself passed the threshold. */
  viaGroup: boolean;
}

export function mergeRelationshipGraph(current: RelationshipGraphData, incoming: RelationshipGraphData): RelationshipGraphData {
  if (current.snapshot?.token !== incoming.snapshot?.token) throw Error('The analysis changed. Refresh before expanding this graph.');
  const nodes = new Map(current.nodes.map(node => [node.uuid, node]));
  for (const node of incoming.nodes) if (nodes.has(node.uuid) || nodes.size < GRAPH_NODE_LIMIT) nodes.set(node.uuid, node);
  const edges = new Map(current.edges.map(edge => [edge.id, edge]));
  for (const edge of incoming.edges) if (nodes.has(edge.a.uuid) && nodes.has(edge.b.uuid) && (edges.has(edge.id) || edges.size < GRAPH_EDGE_LIMIT)) edges.set(edge.id, edge);
  const groups = new Map(current.groups.map(group => [group.id, group]));
  for (const group of incoming.groups) if (groups.has(group.id) || groups.size < 24) {
    const previous = groups.get(group.id);
    const memberCount = Math.max(previous?.memberCount ?? 0, group.memberCount);
    const memberUuids = [...new Set([...(previous?.memberUuids ?? []), ...group.memberUuids])].filter(uuid => nodes.has(uuid));
    groups.set(group.id, { ...previous, ...group, memberCount, memberUuids, membersTruncated: memberCount > memberUuids.length });
  }
  return { ...current, nodes: [...nodes.values()], edges: [...edges.values()], groups: [...groups.values()],
    truncated: { nodes: current.truncated.nodes || incoming.truncated.nodes || current.nodes.length + incoming.nodes.filter(node => !current.nodes.some(existing => existing.uuid === node.uuid)).length > nodes.size,
      edges: current.truncated.edges || incoming.truncated.edges || incoming.edges.some(edge => !edges.has(edge.id)),
      groups: current.truncated.groups || incoming.truncated.groups || incoming.groups.some(group => !groups.has(group.id)) } };
}

/** Selecting a pair outside the first page keeps that pair inside the same caps. */
export function includeSelectedRelationshipPair(graph: RelationshipGraphData, pair: RelationshipPair): RelationshipGraphData {
  if (graph.edges.some(edge => edge.id === pair.id)) return graph;
  const allNodes = new Map([pair.a, pair.b, ...graph.nodes].map(node => [node.uuid, node]));
  const nodes = [...allNodes.values()].slice(0, GRAPH_NODE_LIMIT), ids = new Set(nodes.map(node => node.uuid));
  const edges = [pair, ...graph.edges].filter(edge => ids.has(edge.a.uuid) && ids.has(edge.b.uuid)).slice(0, GRAPH_EDGE_LIMIT);
  const groups = graph.groups.map(group => {
    const memberUuids = group.memberUuids.filter(uuid => ids.has(uuid));
    return { ...group, memberUuids, membersTruncated: group.membersTruncated || memberUuids.length < group.memberCount };
  });
  return { ...graph, nodes, edges, groups, truncated: { ...graph.truncated, nodes: graph.truncated.nodes || allNodes.size > nodes.length, edges: graph.truncated.edges || edges.length < graph.edges.length + 1 } };
}

export function graphNeighbors(edges: RelationshipPair[], id: string): Set<string> {
  const neighbors = new Set([id]);
  for (const edge of edges) { if (edge.a.uuid === id) neighbors.add(edge.b.uuid); if (edge.b.uuid === id) neighbors.add(edge.a.uuid); }
  return neighbors;
}

/**
 * The pairs that count as stronger links: those above IdentAware's threshold, and any
 * pair whose two players share a recorded team or a likely group in this view. An
 * emerging group is itself tentative, so it does not promote its pairs.
 */
export function strongerPairIds(graph: Pick<RelationshipGraphData, 'edges' | 'groups'>): Set<string> {
  const groups = graph.groups.filter(group => group.tier !== 'emerging').map(group => new Set(group.memberUuids));
  return new Set(graph.edges.filter(edge => edge.eligible || groups.some(members => members.has(edge.a.uuid) && members.has(edge.b.uuid))).map(edge => edge.id));
}

/** The backend's order, stronger links first and then by score; `stronger` widens the first tier to group-backed pairs. */
export const compareRelationshipPairs = (a: RelationshipPair, b: RelationshipPair, stronger?: Set<string>) => {
  const tier = (edge: RelationshipPair) => Number(stronger ? stronger.has(edge.id) : edge.eligible);
  return tier(b) - tier(a) || b.score - a.score || a.id.localeCompare(b.id);
};

/** One player's connections, strongest first, one per other player. */
export function rankPlayerConnections(edges: RelationshipPair[], player: string, stronger?: Set<string>): RankedConnection[] {
  const seen = new Set<string>(), ranked: RankedConnection[] = [];
  for (const edge of [...edges].sort((a, b) => compareRelationshipPairs(a, b, stronger))) {
    if (edge.a.uuid !== player && edge.b.uuid !== player) continue;
    const other = edge.a.uuid === player ? edge.b : edge.a;
    if (other.uuid === player || seen.has(other.uuid)) continue;
    seen.add(other.uuid); ranked.push({ edge, other, viaGroup: !edge.eligible && !!stronger?.has(edge.id) });
  }
  return ranked;
}

/**
 * Keeps only a focused player's strongest connections in view. Players listed in
 * `keep` stay regardless of rank, and players reached by expanding a neighbor stay
 * while that neighbor is shown. Group member previews shrink to what is drawn.
 */
export function limitEgoGraph(graph: RelationshipGraphData, limit: number, keep: Iterable<string> = []): RelationshipGraphData {
  const focus = graph.focus.player;
  if (!focus) return graph;
  const ranked = rankPlayerConnections(graph.edges, focus, strongerPairIds(graph)), direct = new Set(ranked.map(item => item.other.uuid));
  const visible = new Set([focus, ...keep, ...ranked.slice(0, Math.max(0, limit)).map(item => item.other.uuid)]);
  for (const edge of graph.edges) for (const [self, other] of [[edge.a.uuid, edge.b.uuid], [edge.b.uuid, edge.a.uuid]])
    if (self !== focus && !direct.has(self) && visible.has(other)) visible.add(self);
  const nodes = graph.nodes.filter(node => visible.has(node.uuid));
  if (nodes.length === graph.nodes.length) return graph;
  const ids = new Set(nodes.map(node => node.uuid));
  return { ...graph, nodes, edges: graph.edges.filter(edge => ids.has(edge.a.uuid) && ids.has(edge.b.uuid)),
    groups: graph.groups.map(group => ({ ...group, memberUuids: group.memberUuids.filter(uuid => ids.has(uuid)) })) };
}

/**
 * A group's display name. A recorded team keeps its team name. A discovered circle
 * is "Anchor +N": its anchor member, named by Argus's own resolver when it can,
 * otherwise by the name IdentAware saw, plus how many others are in it. Analyses
 * from before anchors existed keep the label they came with.
 */
export function groupDisplayName(group: { kind: RelationshipGraphGroup['kind']; label: string; anchor?: string | null; memberCount: number }, resolve: (uuid: string) => string | null | undefined): string {
  if (group.kind === 'observed_team') return group.label || 'Recorded team';
  if (!group.anchor) return group.label || 'Likely group';
  const name = resolve(group.anchor) || group.label.replace(/ \+\d+$/, '') || group.anchor.slice(0, 8);
  return `${name} +${Math.max(0, group.memberCount - 1)}`;
}

/** How a group's outline is drawn on the map: recorded teams solid, likely groups dashed, emerging groups dotted and muted. */
export interface GroupOutline { tone: string; dash: number[] | null; round: boolean; stroke: number; fill: number; selectedStroke: number; selectedFill: number }
export function groupOutline(group: { kind: RelationshipGraphGroup['kind']; tier?: RelationshipGraphGroup['tier'] }): GroupOutline {
  if (group.kind === 'observed_team') return { tone: 'var(--tone-accent-text)', dash: null, round: false, stroke: .2, fill: .035, selectedStroke: .45, selectedFill: .08 };
  if (group.tier === 'emerging') return { tone: 'var(--tone-neutral-text)', dash: [1.5, 4.5], round: true, stroke: .42, fill: .02, selectedStroke: .7, selectedFill: .05 };
  return { tone: 'var(--tone-purple-text)', dash: [6, 5], round: false, stroke: .26, fill: .035, selectedStroke: .5, selectedFill: .085 };
}

export const graphFamilyTone = (families: string[]): string => {
  if (families.some(family => ['membership', 'access'].includes(family))) return 'var(--tone-accent-text)';
  if (families.some(family => ['economy', 'commerce', 'trade', 'payment'].includes(family))) return 'var(--tone-success-text)';
  if (families.some(family => ['place', 'movement'].includes(family))) return 'var(--tone-amber-text)';
  if (families.includes('session')) return 'var(--tone-info-text)';
  return 'var(--tone-purple-text)';
};

/** Convex hull in positive shoelace order (clockwise on screen). Collinear input collapses to its two ends. */
export function convexHull(points: GraphPoint[]): GraphPoint[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y).filter((point, index, all) => !index || point.x !== all[index - 1].x || point.y !== all[index - 1].y);
  if (sorted.length < 3) return sorted;
  const cross = (o: GraphPoint, a: GraphPoint, b: GraphPoint) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: GraphPoint[] = [], upper: GraphPoint[] = [];
  for (const point of sorted) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) lower.pop(); lower.push(point); }
  for (const point of [...sorted].reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) upper.pop(); upper.push(point); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** Rounded outline `pad` away from the points: a circle for one, a capsule for two, a padded convex polygon otherwise. */
export function hullPath(points: GraphPoint[], pad: number): string {
  const hull = convexHull(points), round = (value: number) => Math.round(value * 10) / 10;
  if (!hull.length) return '';
  if (hull.length === 1) { const { x, y } = hull[0]; return `M${round(x - pad)} ${round(y)}A${pad} ${pad} 0 1 1 ${round(x + pad)} ${round(y)}A${pad} ${pad} 0 1 1 ${round(x - pad)} ${round(y)}Z`; }
  const path: string[] = [];
  for (let index = 0; index < hull.length; index++) {
    const a = hull[index], b = hull[(index + 1) % hull.length], c = hull[(index + 2) % hull.length];
    const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy) || 1, nx = dy / length * pad, ny = -dx / length * pad;
    const ex = c.x - b.x, ey = c.y - b.y, next = Math.hypot(ex, ey) || 1;
    path.push(`${index ? 'L' : 'M'}${round(a.x + nx)} ${round(a.y + ny)}L${round(b.x + nx)} ${round(b.y + ny)}A${pad} ${pad} 0 0 1 ${round(b.x + ey / next * pad)} ${round(b.y - ex / next * pad)}`);
  }
  return path.join('') + 'Z';
}

const seed = (id: string) => [...id].reduce((value, character) => (value * 31 + character.charCodeAt(0)) >>> 0, 17) / 4294967295;
const ringRadius = (level: number) => RING_INNER + RING_STEP * level;
const ringCapacity = (level: number) => level ? Math.floor(2 * Math.PI * ringRadius(level) / GRAPH_MIN_SEPARATION) : RING_ONE_CAPACITY;
/** How many of an expanded neighbor's contacts share one ring before the fan moves a ring outward. */
const fanCapacity = (level: number) => Math.max(4, Math.floor(ringCapacity(level) / 4));
const pairKey = (a: string, b: string) => a < b ? `${a}|${b}` : `${b}|${a}`;
const graphAdjacency = (edges: RelationshipPair[]) => {
  const adjacency = new Map<string, Set<string>>();
  const link = (from: string, to: string) => { if (!adjacency.has(from)) adjacency.set(from, new Set()); adjacency.get(from)!.add(to); };
  for (const edge of edges) { link(edge.a.uuid, edge.b.uuid); link(edge.b.uuid, edge.a.uuid); }
  return adjacency;
};
const groupBy = (ids: Iterable<string>, key: (id: string) => string) => {
  const groups = new Map<string, string[]>();
  for (const id of ids) { const value = key(id); if (!groups.has(value)) groups.set(value, []); groups.get(value)!.push(id); }
  return groups;
};

/** Deterministic positions: a focused player's rings, or separated group clusters for an overview. */
export function layoutRelationshipGraph(graph: RelationshipGraphData): GraphLayout {
  const focus = graph.focus.player;
  return focus && graph.nodes.some(node => node.uuid === focus) ? egoLayout(graph, focus) : clusterLayout(graph);
}

/**
 * The focused player sits at the centre and each stronger connection sits on a
 * nearer ring. Every group occupies its own angular wedge, so its outline cannot
 * cross another group's, and players shared by two groups sit where those wedges
 * meet. Players outside any group fill whatever arc is free on their ring, keep a
 * margin from wedge edges, and move outward when a ring is full. Players reached by
 * expanding a neighbor fan outward from that neighbor inside its wedge.
 */
function egoLayout(graph: RelationshipGraphData, focus: string): GraphLayout {
  const anchors = new Map<string, GraphPoint>([[focus, { x: 0, y: 0 }]]);
  const ids = new Set(graph.nodes.map(node => node.uuid));
  const ranked = rankPlayerConnections(graph.edges, focus, strongerPairIds(graph)).map(item => item.other.uuid).filter(id => ids.has(id));
  const order = new Map(ranked.map((id, index) => [id, index]));
  const ring = new Map<string, number>(), cluster = new Map<string, string>();
  for (let index = 0, level = 0, filled = 0; index < ranked.length; index++) {
    if (filled >= ringCapacity(level)) { level++; filled = 0; }
    ring.set(ranked[index], level); filled++;
  }
  const groupIndex = new Map(graph.groups.map((group, index) => [group.id, index]));
  const memberSets = new Map(graph.groups.map(group => [group.id, new Set(group.memberUuids)]));
  for (const group of graph.groups) for (const uuid of group.memberUuids) if (uuid !== focus && order.has(uuid) && !cluster.has(uuid)) cluster.set(uuid, group.id);
  for (const id of ranked) if (!cluster.has(id)) cluster.set(id, SOLO);
  const adjacency = graphAdjacency(graph.edges), strength = new Map<string, number>();
  for (const edge of graph.edges) strength.set(pairKey(edge.a.uuid, edge.b.uuid), Math.max(strength.get(pairKey(edge.a.uuid, edge.b.uuid)) ?? -Infinity, edge.score));
  const children = new Map<string, string[]>(), stray = new Set<string>();
  for (const id of ids) if (id !== focus && !order.has(id)) {
    const parent = [...(adjacency.get(id) ?? [])].filter(other => order.has(other)).sort((a, b) => order.get(a)! - order.get(b)!)[0];
    if (parent) { if (!children.has(parent)) children.set(parent, []); children.get(parent)!.push(id); } else stray.add(id);
  }
  for (const [parent, list] of children) {
    list.sort((a, b) => (strength.get(pairKey(b, parent)) ?? 0) - (strength.get(pairKey(a, parent)) ?? 0) || a.localeCompare(b));
    // An ungrouped neighbor and its expansion share a wedge so they stay together.
    if (cluster.get(parent) === SOLO) cluster.set(parent, `expanded:${parent}`);
    for (let index = 0, level = ring.get(parent)! + 1, filled = 0; index < list.length; index++) {
      if (filled >= fanCapacity(level)) { level++; filled = 0; }
      ring.set(list[index], level); cluster.set(list[index], cluster.get(parent)!); filled++;
      order.set(list[index], order.get(parent)! + .5 + index / (list.length + 1) * .4);
    }
  }
  // Players with no visible link to a ranked connection are not drawn; park them past the last ring.
  const outermost = ring.size ? Math.max(...ring.values()) + 1 : 0;
  [...stray].sort().forEach((id, index) => { ring.set(id, outermost); cluster.set(id, SOLO); order.set(id, ranked.length + index); });
  const byCluster = groupBy(cluster.keys(), id => cluster.get(id)!);
  const groupKeys = [...byCluster.keys()].filter(key => key !== SOLO);
  const shared = (a: string, b: string) => [...(memberSets.get(a) ?? [])].filter(id => id !== focus && memberSets.get(b)?.has(id)).length;
  const pending = [...groupKeys].sort((a, b) => byCluster.get(b)!.length - byCluster.get(a)!.length || (groupIndex.get(a) ?? Infinity) - (groupIndex.get(b) ?? Infinity) || a.localeCompare(b));
  const sequence: string[] = [];
  while (pending.length) {
    const last = sequence[sequence.length - 1];
    const next = last ? pending.reduce((best, key) => shared(last, key) > shared(last, best) ? key : best, pending[0]) : pending[0];
    sequence.push(next); pending.splice(pending.indexOf(next), 1);
  }
  const maxLevel = ring.size ? Math.max(...ring.values()) : 0;
  const need = (key: string, level: number) => byCluster.get(key)!.filter(id => ring.get(id) === level).length * GRAPH_MIN_SEPARATION / ringRadius(level);
  const widths = new Map(groupKeys.map(key => [key, Math.max(...Array.from({ length: maxLevel + 1 }, (_, level) => need(key, level)))]));
  // Wedges share one circle. When they would not all fit they are narrowed evenly, and
  // members a narrowed wedge cannot hold on their rank ring move outward, so the wedge
  // grows longer rather than denser.
  const crowding = Math.max(1, [...widths.values()].reduce((sum, value) => sum + value, 0) / (Math.PI * 2));
  if (crowding > 1) for (const key of groupKeys) {
    const width = widths.get(key)! / crowding, counts = new Map<number, number>();
    widths.set(key, width);
    const capacity = (level: number) => Math.floor(width * ringRadius(level) / GRAPH_MIN_SEPARATION + 1e-9);
    for (const id of [...byCluster.get(key)!].sort((a, b) => order.get(a)! - order.get(b)!)) {
      let level = ring.get(id)!;
      while ((counts.get(level) ?? 0) >= capacity(level) && level < ring.get(id)! + 8) level++;
      ring.set(id, level); counts.set(level, (counts.get(level) ?? 0) + 1);
    }
  }
  const sectors = new Map<string, [number, number]>();
  let start = -Math.PI / 2;
  for (const key of sequence) { const width = widths.get(key)!; sectors.set(key, [start, start + width]); start += width; }
  // A wedge reserves every ring its outline can reach: the chord across a wide wedge sags inward past its innermost member.
  const span = new Map(groupKeys.map(key => {
    const levels = byCluster.get(key)!.map(id => ring.get(id)!), [from, to] = sectors.get(key)!;
    let min = Math.min(...levels);
    const reach = ringRadius(min) * Math.cos(Math.min(Math.PI / 2, (to - from) / 2)) - GRAPH_HULL_PADDING;
    while (min > 0 && ringRadius(min - 1) + 28 > reach) min--;
    return [key, { min, max: Math.max(...levels) }];
  }));
  const freeArcs = (level: number): Array<[number, number]> => {
    const reserved = groupKeys.filter(key => span.get(key)!.min <= level && level <= span.get(key)!.max).map(key => sectors.get(key)!).sort((a, b) => a[0] - b[0]);
    if (!reserved.length) return [[-Math.PI / 2, Math.PI * 1.5]];
    const margin = GRAPH_MIN_SEPARATION / 2 / ringRadius(level), arcs: Array<[number, number]> = [];
    let cursor = -Math.PI / 2;
    for (const [from, to] of reserved) { if (from > cursor + 1e-9) arcs.push([cursor, from]); cursor = Math.max(cursor, to); }
    if (cursor < Math.PI * 1.5 - 1e-9) arcs.push([cursor, Math.PI * 1.5]);
    // The circle closes: the first and last arcs are one arc when nothing is reserved at the top.
    if (arcs.length > 1 && arcs[0][0] === -Math.PI / 2 && arcs[arcs.length - 1][1] === Math.PI * 1.5) { const last = arcs.pop()!; arcs[0] = [last[0] - Math.PI * 2, arcs[0][1]]; }
    return arcs.map(([from, to]): [number, number] => [from + margin, to - margin]).filter(([from, to]) => to > from);
  };
  const soloCount = new Map<number, number>();
  const soloCapacity = (level: number) => Math.floor(freeArcs(level).reduce((sum, [from, to]) => sum + to - from, 0) * ringRadius(level) / GRAPH_MIN_SEPARATION);
  for (const id of [...(byCluster.get(SOLO) ?? [])].filter(id => !stray.has(id)).sort((a, b) => order.get(a)! - order.get(b)!)) {
    let level = ring.get(id)!;
    while ((soloCount.get(level) ?? 0) >= soloCapacity(level) && level < ring.get(id)! + 6) level++;
    ring.set(id, level); soloCount.set(level, (soloCount.get(level) ?? 0) + 1);
  }
  const place = (id: string, angle: number, level: number) => anchors.set(id, { x: Math.cos(angle) * ringRadius(level), y: Math.sin(angle) * ringRadius(level) });
  sequence.forEach((key, position) => {
    const [from, to] = sectors.get(key)!, members = byCluster.get(key)!;
    const previous = memberSets.get(sequence[position - 1]), following = memberSets.get(sequence[position + 1]);
    const side = (id: string) => previous?.has(id) ? -1 : following?.has(id) ? 1 : 0;
    for (const level of new Set(members.map(id => ring.get(id)!))) {
      const row = members.filter(id => ring.get(id) === level).sort((a, b) => side(a) - side(b) || order.get(a)! - order.get(b)!);
      row.forEach((id, index) => place(id, from + (to - from) * (index + .5) / row.length, level));
    }
  });
  for (const [levelKey, members] of groupBy(byCluster.get(SOLO) ?? [], id => String(ring.get(id)))) {
    const level = Number(levelKey), arcs = freeArcs(level), total = arcs.reduce((sum, [from, to]) => sum + to - from, 0);
    members.sort((a, b) => order.get(a)! - order.get(b)!).forEach((id, index) => {
      let offset = total * (index + .5) / members.length;
      for (const [from, to] of arcs) {
        if (offset <= to - from || arcs[arcs.length - 1][0] === from) { place(id, from + Math.min(offset, to - from), level); break; }
        offset -= to - from;
      }
    });
  }
  const levels = [...ring].filter(([id]) => !stray.has(id)).map(([, level]) => level);
  return { anchors, rings: Array.from({ length: levels.length ? Math.max(...levels) + 1 : 0 }, (_, level) => ringRadius(level)) };
}

/**
 * Each recorded group becomes a compact cluster; outside contacts of a group form a
 * satellite beside it and remaining connected players form their own clusters.
 * Clusters are packed so that their padded outlines never overlap and related
 * clusters sit next to each other. A player recorded in two groups is seated in the
 * gap between those two clusters, so both outlines reach it without crossing into
 * the other cluster.
 */
function clusterLayout(graph: RelationshipGraphData): GraphLayout {
  const ids = graph.nodes.map(node => node.uuid), idSet = new Set(ids);
  const adjacency = graphAdjacency(graph.edges), degree = (id: string) => adjacency.get(id)?.size ?? 0;
  const groupIndex = new Map(graph.groups.map((group, index) => [group.id, index]));
  const memberSets = new Map(graph.groups.map(group => [group.id, new Set(group.memberUuids.filter(id => idSet.has(id)))]));
  const ranking = [...graph.groups].sort((a, b) => memberSets.get(b.id)!.size - memberSets.get(a.id)!.size || groupIndex.get(a.id)! - groupIndex.get(b.id)!).map(group => group.id);
  const owner = new Map<string, string>(), bridge = new Map<string, string>();
  for (const key of ranking) for (const id of memberSets.get(key)!) if (!owner.has(id)) owner.set(id, key);
  for (const id of ids) if (!owner.has(id)) {
    const contact = [...(adjacency.get(id) ?? [])].map(other => owner.get(other)).filter((key): key is string => !!key && groupIndex.has(key)).sort((a, b) => groupIndex.get(a)! - groupIndex.get(b)!)[0];
    if (contact) owner.set(id, `satellite:${contact}`);
  }
  for (const id of ids) if (!owner.has(id)) {
    const key = `component:${id}`, queue = [id];
    for (let index = 0; index < queue.length; index++) {
      const current = queue[index];
      if (owner.has(current)) continue;
      owner.set(current, key);
      for (const other of adjacency.get(current) ?? []) if (!owner.has(other)) queue.push(other);
    }
  }
  const owned = groupBy(ids, id => owner.get(id)!);
  for (const id of ids) {
    const other = ranking.find(key => key !== owner.get(id) && memberSets.get(key)!.has(id) && owned.has(key));
    if (other) bridge.set(id, other);
  }
  // A cluster whose every player is shared keeps them, so it still has a body of its own.
  for (const members of owned.values()) if (members.every(id => bridge.has(id))) for (const id of members) bridge.delete(id);
  const spacing = 70, first = GRAPH_MIN_SEPARATION - spacing;
  const slotsFor = (count: number, rotation: number): GraphPoint[] => count === 2
    ? [{ x: -GRAPH_MIN_SEPARATION / 2, y: 0 }, { x: GRAPH_MIN_SEPARATION / 2, y: 0 }]
    : Array.from({ length: count }, (_, index) => { const radius = index ? spacing * Math.sqrt(index) + first : 0; return { x: Math.cos(index * GOLDEN_ANGLE + rotation) * radius, y: Math.sin(index * GOLDEN_ANGLE + rotation) * radius }; });
  const kind = (key: string) => groupIndex.has(key) ? 0 : key.startsWith('satellite:') ? 1 : 2;
  const clusters = [...owned.entries()].map(([key, all]) => {
    const members = all.filter(id => !bridge.has(id)).sort((a, b) => degree(b) - degree(a) || a.localeCompare(b));
    const slots = slotsFor(members.length, seed(key) * Math.PI * 2);
    return { key, members, slots, radius: Math.max(0, ...slots.map(slot => Math.hypot(slot.x, slot.y))) + 34, x: 0, y: 0 };
  }).sort((a, b) => kind(a.key) - kind(b.key) || b.radius - a.radius || a.key.localeCompare(b.key));
  const links = new Map<string, number>();
  const bond = (a: string, b: string, weight: number) => links.set(pairKey(a, b), (links.get(pairKey(a, b)) ?? 0) + weight);
  for (const edge of graph.edges) { const a = owner.get(edge.a.uuid), b = owner.get(edge.b.uuid); if (a && b && a !== b) bond(a, b, 1); }
  const bridges = new Map<string, string[]>();
  for (const [id, other] of bridge) { const key = pairKey(owner.get(id)!, other); if (!bridges.has(key)) bridges.set(key, []); bridges.get(key)!.push(id); bond(owner.get(id)!, other, 10); }
  const affinity = (a: string, b: string) => links.get(pairKey(a, b)) ?? 0;
  const placed: typeof clusters = [];
  for (const item of clusters) {
    const partner = placed.filter(other => affinity(item.key, other.key) > 0).sort((a, b) => affinity(item.key, b.key) - affinity(item.key, a.key))[0];
    const origin = partner ?? { x: 0, y: 0 }, rotation = seed(item.key) * Math.PI * 2;
    // Shared players need room between the two clusters: a full spacing from the nearest player on either side.
    const base = partner ? partner.radius + item.radius + (bridges.has(pairKey(item.key, partner.key)) ? BRIDGE_GAP : CLUSTER_GAP) : 0;
    const fits = (x: number, y: number) => placed.every(other => Math.hypot(other.x - x, other.y - y) >= other.radius + item.radius + (bridges.has(pairKey(item.key, other.key)) ? BRIDGE_GAP : CLUSTER_GAP));
    // Walk outward from the partner (or the centre); at each distance take the free spot nearest the centre so the whole picture stays compact.
    search: for (let step = 0; step < 400; step++) {
      const radius = base + step * 6, turns = Math.max(1, Math.round(Math.PI * 2 * Math.max(radius, 1) / 40));
      const spots = Array.from({ length: turns }, (_, index) => { const angle = rotation + index * Math.PI * 2 / turns; return { x: origin.x + Math.cos(angle) * radius, y: origin.y + Math.sin(angle) * radius }; })
        .sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
      for (const spot of spots) if (fits(spot.x, spot.y)) { item.x = spot.x; item.y = spot.y; break search; }
    }
    placed.push(item);
  }
  const anchors = new Map<string, GraphPoint>(), byKey = new Map(placed.map(item => [item.key, item]));
  for (const item of placed) {
    const partner = placed.filter(other => other !== item && affinity(item.key, other.key) > 0).sort((a, b) => affinity(item.key, b.key) - affinity(item.key, a.key))[0];
    const place = (id: string, slot: GraphPoint) => anchors.set(id, { x: item.x + slot.x, y: item.y + slot.y });
    if (!partner) { item.members.forEach((id, index) => place(id, item.slots[index])); continue; }
    const direction = Math.atan2(partner.y - item.y, partner.x - item.x);
    const facing = (slot: GraphPoint) => slot.x * Math.cos(direction) + slot.y * Math.sin(direction);
    const shared = item.members.filter(id => memberSets.get(partner.key)?.has(id) || [...(adjacency.get(id) ?? [])].some(other => owner.get(other) === partner.key));
    const front = [...item.slots].sort((a, b) => facing(b) - facing(a));
    const back = front.splice(shared.length).sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
    shared.forEach((id, index) => place(id, front[index]));
    item.members.filter(id => !shared.includes(id)).forEach((id, index) => place(id, back[index]));
  }
  for (const [key, members] of bridges) {
    const [a, b] = key.split('|').map(id => byKey.get(id)!);
    const length = Math.hypot(b.x - a.x, b.y - a.y) || 1, ux = (b.x - a.x) / length, uy = (b.y - a.y) / length;
    const along = (length + a.radius - b.radius) / 2;
    members.sort().forEach((id, index) => {
      const across = (index - (members.length - 1) / 2) * GRAPH_MIN_SEPARATION;
      anchors.set(id, { x: a.x + ux * along - uy * across, y: a.y + uy * along + ux * across });
    });
  }
  return { anchors, rings: [] };
}

/** Deterministic seeds avoid a random tangle and preserve positions on expansion. */
export function createGraphParticles(graph: RelationshipGraphData, previous: GraphParticle[] = []): GraphParticle[] {
  const old = new Map(previous.map(point => [point.id, point]));
  const { anchors } = layoutRelationshipGraph(graph);
  return graph.nodes.slice(0, GRAPH_NODE_LIMIT).map(node => {
    const anchor = anchors.get(node.uuid) ?? { x: 0, y: 0 }, prior = old.get(node.uuid), spin = seed(node.uuid) * Math.PI * 2;
    // New players start closer in and visibly settle into their place.
    return { id: node.uuid, x: prior?.x ?? anchor.x * .4 + Math.cos(spin) * 10, y: prior?.y ?? anchor.y * .4 + Math.sin(spin) * 10, vx: 0, vy: 0, anchorX: anchor.x, anchorY: anchor.y };
  });
}

export function stepGraphParticles(points: GraphParticle[], edges: RelationshipPair[], alpha: number): number {
  const lookup = new Map(points.map(point => [point.id, point]));
  for (let i = 0; i < points.length; i++) for (let j = i + 1; j < points.length; j++) {
    const a = points[i], b = points[j], dx = b.x - a.x || .01, dy = b.y - a.y || .01;
    const distance = Math.max(1, Math.hypot(dx, dy));
    if (distance > GRAPH_MIN_SEPARATION * 2) continue;
    const push = (Math.min(10, 2200 / (distance * distance)) + Math.max(0, GRAPH_MIN_SEPARATION - distance) * .085) * alpha;
    a.vx -= dx / distance * push; a.vy -= dy / distance * push;
    b.vx += dx / distance * push; b.vy += dy / distance * push;
  }
  for (const edge of edges) {
    const a = lookup.get(edge.a.uuid), b = lookup.get(edge.b.uuid);
    if (!a || !b) continue;
    const dx = b.x - a.x, dy = b.y - a.y, distance = Math.max(1, Math.hypot(dx, dy));
    // Links rest at their designed length, so dragging a player tugs its connections without collapsing the arrangement.
    const rest = Math.max(GRAPH_MIN_SEPARATION, Math.hypot(b.anchorX - a.anchorX, b.anchorY - a.anchorY));
    const pull = (distance - rest) * .012 * alpha;
    a.vx += dx / distance * pull; a.vy += dy / distance * pull;
    b.vx -= dx / distance * pull; b.vy -= dy / distance * pull;
  }
  let energy = 0;
  for (const point of points) {
    if (point.pinned) { point.x = point.pinned.x; point.y = point.pinned.y; point.vx = 0; point.vy = 0; continue; }
    // A gentle pull toward the designed spot keeps the settle visible for about a second.
    point.vx = (point.vx + (point.anchorX - point.x) * .016 * alpha) * .84;
    point.vy = (point.vy + (point.anchorY - point.y) * .016 * alpha) * .84;
    point.x += point.vx; point.y += point.vy;
    energy += Math.abs(point.vx) + Math.abs(point.vy);
  }
  return energy;
}

export function fitGraphCamera(points: GraphPoint[], width: number, height: number): GraphCamera {
  if (!points.length) return { x: width / 2, y: height / 2, scale: 1 };
  const minX = Math.min(...points.map(point => point.x)) - 90, maxX = Math.max(...points.map(point => point.x)) + 90;
  const minY = Math.min(...points.map(point => point.y)) - 90, maxY = Math.max(...points.map(point => point.y)) + 90;
  const scale = Math.max(.16, Math.min(1.3, (width - 40) / Math.max(1, maxX - minX), (height - 60) / Math.max(1, maxY - minY)));
  return { x: width / 2 - (minX + maxX) / 2 * scale, y: height / 2 - (minY + maxY) / 2 * scale, scale };
}

export function zoomGraphCamera(camera: GraphCamera, scale: number, origin: GraphPoint): GraphCamera {
  const bounded = Math.max(.16, Math.min(2.5, scale));
  return { scale: bounded, x: origin.x - (origin.x - camera.x) * bounded / camera.scale,
    y: origin.y - (origin.y - camera.y) * bounded / camera.scale };
}
